import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AccountCircle } from '@mui/icons-material';
import { Box } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { CreatePage, FuseInput, FuseSelect } from '/src/components';
import { useFilterStore, useUserStore } from '/src/context';
import { toastMessages } from '/src/lib/messages';
import { CreateUserForm, DataModelType, FormErrors, UserRole } from '/src/lib/types';

/**
 * Create User Page
 */
export const CreateUserPage = observer(() => {
  /** Context **/
  const userStore = useUserStore();
  const filterStore = useFilterStore();
  const navigate = useNavigate();

  /** State **/
  const [formValues, setFormValues] = useState<CreateUserForm>({
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: UserRole.Company,
    company: undefined,
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  /** Computed **/
  const companies = useMemo(() => filterStore.companyListGet() ?? [], [filterStore._companyList]);
  const lenders = useMemo(() => {
    const l = filterStore.lenderListGet();
    if (Array.isArray(l)) {
      return l;
    } else if (l && l._id) {
      return [l];
    }
    return [];
  }, [filterStore._lenderList]);

  /** Methods **/

  /**
   * Updates a form value. Designed so that, in a form field's `onChange` method,
   * you pass this function with the name of the prop you want to update, and it
   * returns a function that will automatically handle saving the returned value.
   *
   * @param key Name of the form prop to update.
   */
  const updateFormValue = (key: string) => (value: any) => {
    setFormValues((prevFormValues) => ({
      ...prevFormValues,
      [key]: value,
    }));
    // Clear error when field is updated
    if (formErrors[key]) {
      setFormErrors((prevFormErrors) => ({
        ...prevFormErrors,
        [key]: '',
      }));
    }
  };

  /**
   * Validates the form data. If errors are detected, it saves them to `formErrors` and returns false.
   *
   * @returns Boolean indicating whether the data is valid
   */
  const validateForm = () => {
    const errors: FormErrors = {};

    if (!formValues.username) {
      errors.username = 'Username is required';
    }
    if (!formValues.firstName) {
      errors.firstName = 'First Name is required';
    }
    if (!formValues.lastName) {
      errors.lastName = 'Last Name is required';
    }
    if (!formValues.password) {
      errors.password = 'Password is required';
    }
    if (!formValues.email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formValues.email)) {
      errors.email = 'Email is invalid';
    }
    if (!formValues.role) {
      errors.role = 'User Type is required';
    }
    if ((formValues.role === UserRole.Company || formValues.role === UserRole.Investor) && !formValues.company) {
      errors.company = 'Company is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Creates the User.
   *
   * @remarks
   * Redirects to the User's Detail page afterwards.
   */
  const create = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors first');
      return;
    }

    try {
      // Save the User
      const user = await userStore.create(formValues);
      toast.success('User created successfully');
      navigate(`/users/${user._id}`);

      // Reset the form
      setFormValues({
        username: '',
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        role: UserRole.Company,
        company: undefined,
      });
      setFormErrors({});
    } catch (error) {
      console.error(error);
      toast.error(toastMessages.createUser.error);
    }
  };

  const changeUserType = (role: UserRole) => {
    updateFormValue('role')(role);
    updateFormValue('company')(undefined);
  };

  /** Render **/
  return (
    <CreatePage
      modelType={DataModelType.User}
      onCreate={create}
      width="50%"
      createVerb="Create"
      createIcon={<AccountCircle className="md-icon material-icons" />}
    >
      <Box display="flex" justifyContent="center" alignItems="center">
        <FuseInput
          label="Username"
          value={formValues.username}
          updateValue={updateFormValue('username')}
          error={formErrors.username}
          required
        />
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center" gap="16px">
        <FuseInput
          label="First Name"
          value={formValues.firstName}
          updateValue={updateFormValue('firstName')}
          error={formErrors.firstName}
          required
        />
        <FuseInput
          label="Last Name"
          value={formValues.lastName}
          updateValue={updateFormValue('lastName')}
          error={formErrors.lastName}
          required
        />
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center">
        <FuseInput
          label="Password"
          type="password"
          value={formValues.password}
          updateValue={updateFormValue('password')}
          error={formErrors.password}
          required
        />
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center">
        <FuseInput
          label="Email"
          type="email"
          value={formValues.email}
          updateValue={updateFormValue('email')}
          error={formErrors.email}
          required
        />
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center">
        <FuseSelect
          label="User Type"
          placeholder="Select User Type"
          value={formValues.role}
          updateValue={changeUserType}
          error={formErrors.role}
          options={[
            { value: UserRole.Admin, label: 'Admin' },
            { value: UserRole.Company, label: 'Company' },
            { value: UserRole.Investor, label: 'Investor' },
          ]}
          required
          boxProps={{ 'aria-label': 'Select a type for this User.' }}
        />
      </Box>

      <Box display="flex" justifyContent="center" alignItems="center">
        <FuseSelect
          label="User Company"
          placeholder="Select User Company"
          value={formValues.company}
          updateValue={updateFormValue('company')}
          error={formErrors.company}
          options={(userStore.isLender ? lenders : companies).map((company) => ({
            value: company._id,
            label: company.name,
          }))}
          required
          disabled={formValues.role === UserRole.Admin}
          boxProps={{ 'aria-label': 'Select a Company to associate this user with.' }}
        />
      </Box>
    </CreatePage>
  );
});
